import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getCurrentUser } from '../api/me';
import type { CurrentUser } from '../api/types';

interface CurrentUserState {
  /** Null until the answer arrives, and if it never does. */
  user: CurrentUser | null;
  loading: boolean;
  /** Re-reads the answer after something that changes it, such as a first contact code creating a personal space. */
  refresh: () => void;
}

const CurrentUserContext = createContext<CurrentUserState>({ user: null, loading: true, refresh: () => undefined });

/**
 * Who is signed in, fetched once for the whole shell: the top bar drops the pages this person has nothing to
 * do on, and the dashboard has to explain itself to somebody who belongs to no group, and the two must not be
 * able to disagree.
 *
 * A failed read leaves the user null, which every reader treats as "carry on": a network blip must not take
 * navigation away.
 */
export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<CurrentUserState, 'refresh'>>({ user: null, loading: true });
  const [generation, setGeneration] = useState(0);
  const refresh = useCallback(() => setGeneration((value) => value + 1), []);

  useEffect(() => {
    let current = true;

    getCurrentUser()
      .then((user) => {
        if (current) {
          setState({ user, loading: false });
        }
      })
      .catch(() => {
        if (current) {
          setState((previous) => ({ user: generation === 0 ? null : previous.user, loading: false }));
        }
      });

    return () => {
      current = false;
    };
  }, [generation]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

export function useCurrentUser(): CurrentUserState {
  return useContext(CurrentUserContext);
}

/**
 * Whether this person is in a group at all - the question behind which pages to offer and whether the
 * dashboard has anything to show. Unknown counts as yes: the alternative is taking things away because a
 * request failed.
 */
export function belongsToAGroup(user: CurrentUser | null): boolean {
  if (!user) {
    return true;
  }

  return user.isGroupMemberSomewhere || user.isGroupAdminSomewhere || user.isSuperAdmin;
}
